// W56.0 B — the sheet detent model (ADR-0198).
//
// A **detent** is a height a bottom-anchored sheet rests at. `DESIGN.md §11.1 #2`
// has specified the shape since the sheet was designed — 「Medium 預設 → Large
// 可拖」 — and `§18.7` recorded the opposite (「不分 small/medium」), so the two
// sections contradicted each other and the code implemented neither: every
// sheet emitted `data-detent="large"` as a constant.
//
// This module is the model, kept out of the component on purpose: the component
// is already the largest file in `patterns/`, and the geometry rules are the
// kind of thing that should be **one** place with its own tests rather than a
// set of `if`s inside a gesture handler.
//
// ## The heights are caps, not forced heights
//
// This is the one place where this deviates from iOS, and the reason is
// concrete: heshe has **21** mobile sheets, and most of them are short — the
// mood picker, the status picker, a confirm dialog, the notification inbox with
// three rows. iOS forces a `.medium()` detent to be exactly half the screen
// whatever the content, so a short sheet there gets half a screen of empty
// space. Applying that here would put a large blank area under most of the
// app's sheets.
//
// So a detent here is a **maximum height**: the sheet is `min(content, cap)`.
// A sheet with more content than the cap is scrollable inside it and can be
// pulled up to the next detent; a sheet with less content than the cap is
// exactly as tall as its content, because there is nothing to reveal.
//
// The trade: a short sheet's detent does not change its appearance, only its
// `data-detent` value. The mechanism is identical for every sheet; what differs
// is whether there is anything for it to reveal.

/** The three detents, from smallest to largest. */
export type SheetDetent = "collapsed" | "medium" | "large";

/**
 * The three resting heights, as a fraction of the viewport height.
 *
 * 30% / 60% / 90% are the values `DESIGN.md §11.1 #2` and Apple's
 * `sheets.md` align on (medium ≈ half, large ≈ full). They have **not** been
 * measured on a real 390×844 device yet — `docs/spec/w56.0-mobile-polish.md`
 * §15 lists that as still open, and it needs a real thumb, not jsdom.
 */
export const DETENT_FRACTIONS: Record<SheetDetent, number> = {
  collapsed: 0.3,
  medium: 0.6,
  large: 0.9,
};

/** Cycle order. The grabber walks this and wraps around. */
export const DETENT_ORDER: readonly SheetDetent[] = ["collapsed", "medium", "large"];

/** The mobile sheet opens at `medium` — `DESIGN.md §11.1 #2`. */
export const DEFAULT_DETENT: SheetDetent = "medium";

export function isSheetDetent(value: unknown): value is SheetDetent {
  return typeof value === "string" && (DETENT_ORDER as readonly string[]).includes(value);
}

/**
 * The next detent in the grabber's cycle.
 *
 * `medium → large → collapsed → medium`. The order is "up first" because the
 * common case is a sheet that was opened short and wants more room; reaching
 * `collapsed` takes two taps rather than one, which is the right way round for
 * the gesture that hides content.
 */
export function nextDetent(current: SheetDetent): SheetDetent {
  const index = DETENT_ORDER.indexOf(current);
  return DETENT_ORDER[(index + 1) % DETENT_ORDER.length]!;
}

/** A detent's cap, in px, for a given viewport height. */
export function detentHeightPx(detent: SheetDetent, viewportHeightPx: number): number {
  return Math.round(DETENT_FRACTIONS[detent] * viewportHeightPx);
}

/**
 * The detent whose cap is closest to a projected height.
 *
 * The projection is a **fraction of the viewport**, not a pixel height,
 * because the caps are fractions: comparing a projected pixel height against a
 * fraction would compare two different units.
 *
 * Ties go to the earlier detent in `DETENT_ORDER` (smallest first), which is
 * the conservative direction: landing on a smaller sheet shows less, and the
 * user can always pull it open again.
 */
export function closestDetent(projectedFraction: number): SheetDetent {
  let best: SheetDetent = DEFAULT_DETENT;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const detent of DETENT_ORDER) {
    const delta = Math.abs(DETENT_FRACTIONS[detent] - projectedFraction);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = detent;
    }
  }
  return best;
}

/**
 * The detent a drag should snap to.
 *
 * `dragDeltaPx` is the signed finger displacement: **positive is downward**.
 * The projection is on the **cap**, not on the sheet's rendered height, and
 * that distinction matters: a short sheet's rendered height is its content
 * height, so projecting on it would report a drag of 100px on a 200px-tall
 * sheet as 「almost nothing left」 and collapse it — a claim the sheet's
 * appearance does not back up, because a short sheet has nothing to collapse.
 */
export function snapDetent(
  current: SheetDetent,
  dragDeltaPx: number,
  viewportHeightPx: number,
): SheetDetent {
  if (viewportHeightPx <= 0) return current;
  const cap = detentHeightPx(current, viewportHeightPx);
  const projected = cap - dragDeltaPx;
  const fraction = projected / viewportHeightPx;
  return closestDetent(fraction);
}
